import { useRef, useState, MouseEvent, ReactElement, useEffect } from 'react';
import cn from 'clsx';
import Popper from '@material-ui/core/Popper';
import { makeStyles } from '@material-ui/styles';

import { Theme } from '../../theme/types';
import Fade, { duration } from '../tooltip/Fade';

type OverflowTooltipProps = {
    content: string | ReactElement;
    className?: string;
    disablePortals?: boolean;
};

const TOOLTIP_MAX_WIDTH = 250;

const OverflowTooltip: React.FC<OverflowTooltipProps> = ({
    content = '',
    className,
    children,
    disablePortals = false,
}) => {
    const classes = useStyles();
    const tooltipAnchor = useRef(null);
    const clampedEl = useRef(null);
    const [renderTooltip, setRenderTooltip] = useState(false);
    const [open, setOpen] = useState(false);

    // detecting CSS line-clamp by javascript is done here by comparing
    // the scrollHeight and the clientHeight of the "clamped" element.
    const isTextClamped = (e: MouseEvent<HTMLElement>): boolean => {
        if (clampedEl.current) {
            const { scrollHeight = 0, clientHeight = 0 } = clampedEl.current as HTMLElement;

            return scrollHeight > clientHeight;
        }
        // comparing heights of the hovered element if ref to clamped element was not provided
        const { scrollHeight = 0, clientHeight = 0 } = (e.target as HTMLElement) || {};

        return scrollHeight > clientHeight;
    };

    const onMouseEnter = (e: MouseEvent<HTMLElement>) => {
        if (!renderTooltip && isTextClamped(e)) {
            setRenderTooltip(true);
            setOpen(true);
        }
    };

    const onMouseLeave = () => {
        setOpen(false);
    };

    //this is needed to prevent the tooltip from closing without animation
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (!open) {
                setRenderTooltip(false);
            }
        }, duration);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [open]);

    return (
        <div
            className={classes.container}
            ref={tooltipAnchor}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {typeof children === 'function' ? children(clampedEl) : children}
            {renderTooltip && (
                <Popper
                    open={open}
                    anchorEl={tooltipAnchor.current}
                    placement="top"
                    disablePortal={disablePortals}
                    className={classes.popper}
                    modifiers={{
                        flip: {
                            enabled: false,
                        },
                        arrow: {
                            enabled: false,
                        },
                    }}
                    transition
                >
                    {({ TransitionProps }) => (
                        <Fade {...TransitionProps}>
                            <div className={cn(classes.paper, className)}>{content}</div>
                        </Fade>
                    )}
                </Popper>
            )}
        </div>
    );
};

const useStyles = makeStyles<Theme>(
    ({ palette, typography }) => ({
        container: {
            display: 'inline-flex',
            cursor: 'default',
        },
        popper: {
            zIndex: 10002,
        },
        paper: {
            ...typography.t81,
            borderRadius: '1.2em',
            filter: 'drop-shadow(0px 4px 14px rgba(0, 0, 0, 0.1))',
            '-webkit-filter': 'drop-shadow(0px 4px 14px rgba(0, 0, 0, 0.1))',
            padding: '0.6em 1.2em',
            wordBreak: 'break-word',
            maxWidth: TOOLTIP_MAX_WIDTH,
            backgroundColor: palette.greys['100'],
            color: palette.greys['500'],
            boxShadow: 'none',
            width: 'fit-content',
        },
    }),
    { classNamePrefix: 'overflow-tooltip' }
);

export default OverflowTooltip;
